import { create } from 'zustand';
import { addDays, format, parse } from 'date-fns';
import { SERVER_FORMAT_DATE } from '@/common/app/constants/timeConstants';
import type { IAvailability } from '@/common/service/api/Availability/Availability.domain';

function datesInRange(from: string, to: string): string[] {
    const dates: string[] = [];
    const start = parse(from, SERVER_FORMAT_DATE, new Date());
    const end = parse(to, SERVER_FORMAT_DATE, new Date());
    for (let d = start; d <= end; d = addDays(d, 1)) {
        dates.push(format(d, SERVER_FORMAT_DATE));
    }
    return dates;
}

export function isTripAvailable(
    availabilityMap: Record<string, IAvailability>,
    tripId: string,
    from: string,
    to: string
): boolean {
    const record = availabilityMap[tripId];
    if (!record) return false;
    const dates = datesInRange(from, to);
    return dates.some((date) => record.days_availability[date] === 1);
}

async function fetchBatchAvailability(
    ids: string[],
    from: string,
    to: string
): Promise<IAvailability[]> {
    if (!ids.length) return [];
    const query = `trip_ids=${ids.join(',')}&from=${from}&to=${to}`;
    try {
        const res = await fetch(`/api/cache-availability-short/?${query}`);
        if (!res.ok) return [];
        return (await res.json()) as IAvailability[];
    } catch {
        return [];
    }
}

async function fetchPriorityAvailability(
    ids: string[],
    from: string,
    to: string,
    set: (
        fn: (state: { availabilityMap: Record<string, IAvailability> }) => {
            availabilityMap: Record<string, IAvailability>;
        }
    ) => void
): Promise<void> {
    await Promise.allSettled(
        ids.map(async (id) => {
            try {
                const query = `trip_ids=${id}&from=${from}&to=${to}`;
                const res = await fetch(`/api/cache-availability-short/?${query}`);
                if (res.ok) {
                    const data = (await res.json()) as IAvailability[];
                    if (data[0]) {
                        set((state) => ({
                            availabilityMap: { ...state.availabilityMap, [data[0].id]: data[0] },
                        }));
                    }
                }
            } catch {
                /* ignore */
            }
        })
    );
}

type TCacheAvailabilityStore = {
    availabilityMap: Record<string, IAvailability>;
    currentRange: { from: string; to: string } | null;
    fetchAvailability: (
        tripIds: string[],
        from: string,
        to: string,
        isPriority?: boolean
    ) => Promise<void>;
    clearAvailability: () => void;
};

export const useCacheAvailabilityStore = create<TCacheAvailabilityStore>((set, get) => ({
    availabilityMap: {},
    currentRange: null,

    fetchAvailability: async (tripIds, from, to, isPriority) => {
        if (!tripIds.length) return;

        const { currentRange } = get();

        if (currentRange && (currentRange.from !== from || currentRange.to !== to)) {
            set({ availabilityMap: {}, currentRange: { from, to } });
        } else if (!currentRange) {
            set({ currentRange: { from, to } });
        }

        const newIds = tripIds.filter((id) => !get().availabilityMap[id]);
        if (!newIds.length) return;

        if (isPriority) {
            await fetchPriorityAvailability(newIds, from, to, set);
        } else {
            const results = await fetchBatchAvailability(newIds, from, to);
            set((state) => ({
                availabilityMap: results.reduce(
                    (acc, item) => {
                        acc[item.id] = item;
                        return acc;
                    },
                    { ...state.availabilityMap }
                ),
            }));
        }
    },

    clearAvailability: () => {
        set({ availabilityMap: {}, currentRange: null });
    },
}));
